// ============================================================
// Motor de cálculo de riesgo ergonómico — Método ROSA
// (Rapid Office Strain Assessment)
//
// Fuente: Sonne, M., Villalta, D.L. & Andrews, D.M. (2012).
// Development and evaluation of an office ergonomic risk checklist:
// the Rapid Office Strain Assessment (ROSA). Applied Ergonomics,
// 43(1), 98-108.
//
// Nota interna (solo para verificación cruzada — no citar estas
// fuentes como "la fuente" en ningún otro lugar del código ni en el
// PDF/UI): las 5 tablas de combinación fueron verificadas cruzando
// dos fuentes secundarias independientes — el documento oficial "ROSA
// Instructions and Worksheet" de Cornell University (alojado con
// autorización de los autores) y la Nota Técnica de Prevención
// NTP 1173 (2022) del INSST — y ambas coinciden exactamente en las
// Tablas B, C, D y E; la Tabla A se tomó de la NTP 1173 por ser la
// que pasa el autochequeo contra su propio ejemplo resuelto.
//
// Motor de cálculo puro, sin UI:
//   - Sección A: silla (altura y profundidad del asiento,
//     reposabrazos, respaldo) + tiempo de uso (Tabla F) → Tabla A
//   - Sección B: teléfono y pantalla → Tabla B
//   - Sección C: ratón y teclado → Tabla C
//   - Sección D: pantalla/teléfono + ratón/teclado (Sección B x
//     Sección C) → Tabla D
//   - Puntuación final ROSA (Silla x Pantalla/Periféricos, Tabla E) y
//     nivel de actuación (>= 5 → acción requerida)
// ============================================================

function clamp(valor: number, min: number, max: number): number {
  return Math.min(Math.max(valor, min), max);
}

// ===== Tiempo de uso (Tabla F) — transversal a Silla, Teléfono, Pantalla, Ratón y Teclado =====

export type TiempoUsoRosa =
  | 'menos_30min_o_menos_1h_dia'
  | 'entre_30min_1h_o_1_4h_dia'
  | 'mas_1h_o_mas_4h_dia';

const PUNTUACION_TIEMPO_USO: Record<TiempoUsoRosa, number> = {
  menos_30min_o_menos_1h_dia: -1,
  entre_30min_1h_o_1_4h_dia: 0,
  mas_1h_o_mas_4h_dia: 1,
};

// ============================================================
// SECCIÓN A: SILLA
// ============================================================

export type AlturaAsientoRosa =
  | 'rodillas_90'
  | 'bajo_rodillas_menor_90'
  | 'alto_rodillas_mayor_90'
  | 'sin_contacto_suelo';

export type ProfundidadAsientoRosa = 'neutra_8cm' | 'menor_8cm' | 'mayor_8cm';

export type ReposabrazosRosa =
  | 'neutro_90_relajado'
  | 'desviacion_alto_o_bajo'
  // sin_reposabrazos se trata como 'desviacion_alto_o_bajo' (score 2),
  // según el criterio documentado en la NTP para sillas sin reposabrazos
  | 'sin_reposabrazos';

export type RespaldoRosa =
  | 'neutro_95_110'
  | 'no_se_utiliza'
  | 'sin_apoyo_lumbar_o_inadecuado'
  | 'inclinacion_fuera_95_110';

export interface EntradaSillaRosa {
  // A-1 Altura del asiento
  alturaAsiento: AlturaAsientoRosa;
  alturaNoRegulable: boolean; // +1
  espacioInsuficienteBajoMesa: boolean; // +1
  // A-2 Profundidad del asiento
  profundidadAsiento: ProfundidadAsientoRosa;
  profundidadNoRegulable: boolean; // +1
  // A-3 Reposabrazos
  reposabrazos: ReposabrazosRosa;
  reposabrazosDemasiadoAnchos: boolean; // +1
  reposabrazosNoRegulables: boolean; // +1
  reposabrazosBordesDurosOFilosos: boolean; // +1
  // A-4 Respaldo
  respaldo: RespaldoRosa;
  respaldoSuperficieAlta: boolean; // +1 (hombros encogidos)
  respaldoNoRegulable: boolean; // +1
  // Tiempo de uso (Tabla F) — transversal
  tiempoUso: TiempoUsoRosa;
}

const PUNTUACION_ALTURA_ASIENTO: Record<AlturaAsientoRosa, number> = {
  rodillas_90: 1,
  bajo_rodillas_menor_90: 2,
  alto_rodillas_mayor_90: 2,
  sin_contacto_suelo: 3,
};

const PUNTUACION_PROFUNDIDAD_ASIENTO: Record<ProfundidadAsientoRosa, number> = {
  neutra_8cm: 1,
  menor_8cm: 2,
  mayor_8cm: 2,
};

const PUNTUACION_REPOSABRAZOS: Record<ReposabrazosRosa, number> = {
  neutro_90_relajado: 1,
  desviacion_alto_o_bajo: 2,
  sin_reposabrazos: 2,
};

const PUNTUACION_RESPALDO: Record<RespaldoRosa, number> = {
  neutro_95_110: 1,
  no_se_utiliza: 2,
  sin_apoyo_lumbar_o_inadecuado: 2,
  inclinacion_fuera_95_110: 2,
};

export function calcularPuntuacionA1(
  alturaAsiento: AlturaAsientoRosa,
  alturaNoRegulable: boolean,
  espacioInsuficienteBajoMesa: boolean
): number {
  let puntuacion = PUNTUACION_ALTURA_ASIENTO[alturaAsiento];
  if (alturaNoRegulable) puntuacion += 1;
  if (espacioInsuficienteBajoMesa) puntuacion += 1;
  return puntuacion;
}

export function calcularPuntuacionA2(
  profundidadAsiento: ProfundidadAsientoRosa,
  profundidadNoRegulable: boolean
): number {
  let puntuacion = PUNTUACION_PROFUNDIDAD_ASIENTO[profundidadAsiento];
  if (profundidadNoRegulable) puntuacion += 1;
  return puntuacion;
}

export function calcularPuntuacionA3(
  reposabrazos: ReposabrazosRosa,
  demasiadoAnchos: boolean,
  noRegulables: boolean,
  bordesDurosOFilosos: boolean
): number {
  let puntuacion = PUNTUACION_REPOSABRAZOS[reposabrazos];
  if (demasiadoAnchos) puntuacion += 1;
  if (noRegulables) puntuacion += 1;
  if (bordesDurosOFilosos) puntuacion += 1;
  return puntuacion;
}

export function calcularPuntuacionA4(
  respaldo: RespaldoRosa,
  superficieAlta: boolean,
  noRegulable: boolean
): number {
  let puntuacion = PUNTUACION_RESPALDO[respaldo];
  if (superficieAlta) puntuacion += 1;
  if (noRegulable) puntuacion += 1;
  return puntuacion;
}

// Tabla A ROSA (silla) — extraída de la NTP 1173 (2022), verificada
// contra su propio ejemplo resuelto (fila 4/columna 6 → 5). Filas =
// "Asiento: altura+profundidad" (suma de A-1+A-2, clampeado 2-8),
// columnas = "Reposabrazos+respaldo" (suma de A-3+A-4, clampeado 2-9).
// No modificar sin repetir esa verificación.
const TABLA_A_SILLA: number[][] = [
  [2, 2, 3, 4, 5, 6, 7, 8], // fila 2
  [2, 2, 3, 4, 5, 6, 7, 8], // fila 3
  [3, 3, 3, 4, 5, 6, 7, 8], // fila 4
  [4, 4, 4, 4, 5, 6, 7, 8], // fila 5
  [5, 5, 5, 5, 6, 7, 8, 9], // fila 6
  [6, 6, 6, 7, 7, 8, 8, 9], // fila 7
  [7, 7, 7, 8, 8, 9, 9, 9], // fila 8
];

export function calcularCeldaTablaA(sumaAlturaProfundidad: number, sumaReposabrazosRespaldo: number): number {
  const fila = clamp(sumaAlturaProfundidad, 2, 8) - 2;
  const columna = clamp(sumaReposabrazosRespaldo, 2, 9) - 2;
  return TABLA_A_SILLA[fila][columna];
}

export function calcularPuntuacionSilla(input: EntradaSillaRosa): number {
  const a1 = calcularPuntuacionA1(input.alturaAsiento, input.alturaNoRegulable, input.espacioInsuficienteBajoMesa);
  const a2 = calcularPuntuacionA2(input.profundidadAsiento, input.profundidadNoRegulable);
  const a3 = calcularPuntuacionA3(
    input.reposabrazos,
    input.reposabrazosDemasiadoAnchos,
    input.reposabrazosNoRegulables,
    input.reposabrazosBordesDurosOFilosos
  );
  const a4 = calcularPuntuacionA4(input.respaldo, input.respaldoSuperficieAlta, input.respaldoNoRegulable);

  // Sin clamp final: puede superar ligeramente el rango de la tabla
  // por el ajuste de tiempo, igual que el ejemplo de la NTP (5+1=6).
  return calcularCeldaTablaA(a1 + a2, a3 + a4) + PUNTUACION_TIEMPO_USO[input.tiempoUso];
}

// ============================================================
// SECCIÓN B: PANTALLA Y TELÉFONO
// ============================================================

export type TelefonoRosa = 'neutro_1mano_manosLibres' | 'alejado_mayor_30cm';
export type PantallaRosa = 'neutra_40_75cm_altura_ojos' | 'baja_menor_30grados' | 'alta_extension_cuello';

export interface EntradaPantallaTelefonoRosa {
  // B-1 Teléfono
  telefono: TelefonoRosa;
  telefonoSujecionHombroCuello: boolean; // +2
  telefonoSinManosLibres: boolean; // +1
  telefonoTiempoUso: TiempoUsoRosa;
  // B-2 Pantalla
  pantalla: PantallaRosa;
  pantallaDistanciaMayor75cm: boolean; // +1
  pantallaGiroCuello: boolean; // +1
  pantallaSinPortadocumentos: boolean; // +1
  pantallaReflejos: boolean; // +1
  pantallaTiempoUso: TiempoUsoRosa;
}

const PUNTUACION_TELEFONO_BASE: Record<TelefonoRosa, number> = {
  neutro_1mano_manosLibres: 1,
  alejado_mayor_30cm: 2,
};

const PUNTUACION_PANTALLA_BASE: Record<PantallaRosa, number> = {
  neutra_40_75cm_altura_ojos: 1,
  baja_menor_30grados: 2,
  alta_extension_cuello: 3,
};

export function calcularPuntuacionTelefono(input: EntradaPantallaTelefonoRosa): number {
  let puntuacion = PUNTUACION_TELEFONO_BASE[input.telefono];
  if (input.telefonoSujecionHombroCuello) puntuacion += 2;
  if (input.telefonoSinManosLibres) puntuacion += 1;
  puntuacion += PUNTUACION_TIEMPO_USO[input.telefonoTiempoUso];
  return Math.max(puntuacion, 0);
}

export function calcularPuntuacionPantalla(input: EntradaPantallaTelefonoRosa): number {
  let puntuacion = PUNTUACION_PANTALLA_BASE[input.pantalla];
  if (input.pantallaDistanciaMayor75cm) puntuacion += 1;
  if (input.pantallaGiroCuello) puntuacion += 1;
  if (input.pantallaSinPortadocumentos) puntuacion += 1;
  if (input.pantallaReflejos) puntuacion += 1;
  puntuacion += PUNTUACION_TIEMPO_USO[input.pantallaTiempoUso];
  return puntuacion;
}

// Tabla B ROSA (teléfono x pantalla) — verificada idéntica entre
// Cornell y NTP 1173. Filas = Teléfono (0-6, clampeado), columnas =
// Pantalla (0-8, clampeado). No modificar sin repetir esa verificación.
const TABLA_B_TELEFONO_PANTALLA: number[][] = [
  [1, 1, 1, 2, 3, 4, 5, 6, 6],
  [1, 1, 2, 2, 3, 4, 5, 6, 6],
  [1, 2, 2, 3, 3, 4, 6, 7, 7],
  [2, 2, 3, 3, 4, 5, 6, 8, 8],
  [3, 3, 4, 4, 5, 6, 7, 8, 8],
  [4, 4, 5, 5, 6, 7, 8, 9, 9],
  [5, 5, 6, 7, 8, 8, 9, 9, 9],
];

export function calcularCeldaTablaB(telefono: number, pantalla: number): number {
  const fila = clamp(telefono, 0, 6);
  const columna = clamp(pantalla, 0, 8);
  return TABLA_B_TELEFONO_PANTALLA[fila][columna];
}

export function calcularPuntuacionSeccionB(input: EntradaPantallaTelefonoRosa): number {
  const telefono = calcularPuntuacionTelefono(input);
  const pantalla = calcularPuntuacionPantalla(input);
  return calcularCeldaTablaB(telefono, pantalla);
}

// ============================================================
// SECCIÓN C: RATÓN Y TECLADO
// ============================================================

export type RatonRosa = 'neutro_alineado_hombro' | 'desviacion_no_alineado';
export type TecladoRosa = 'neutro_muneca_recta' | 'desviacion_extension_mayor_15';

export interface EntradaRatonTecladoRosa {
  // C-1 Ratón
  raton: RatonRosa;
  ratonPequenoAgarrePinza: boolean; // +1
  ratonTecladoDiferentesAlturas: boolean; // +2
  ratonReposamanosDuro: boolean; // +1
  ratonTiempoUso: TiempoUsoRosa;
  // C-2 Teclado
  teclado: TecladoRosa;
  tecladoDesviacionAlEscribir: boolean; // +1
  tecladoElevadoHombrosEncogidos: boolean; // +1
  tecladoAlcancePorEncimaCabeza: boolean; // +1
  tecladoSoporteNoAjustable: boolean; // +1
  tecladoTiempoUso: TiempoUsoRosa;
}

const PUNTUACION_RATON_BASE: Record<RatonRosa, number> = {
  neutro_alineado_hombro: 1,
  desviacion_no_alineado: 2,
};

const PUNTUACION_TECLADO_BASE: Record<TecladoRosa, number> = {
  neutro_muneca_recta: 1,
  desviacion_extension_mayor_15: 2,
};

export function calcularPuntuacionRaton(input: EntradaRatonTecladoRosa): number {
  let puntuacion = PUNTUACION_RATON_BASE[input.raton];
  if (input.ratonPequenoAgarrePinza) puntuacion += 1;
  if (input.ratonTecladoDiferentesAlturas) puntuacion += 2;
  if (input.ratonReposamanosDuro) puntuacion += 1;
  puntuacion += PUNTUACION_TIEMPO_USO[input.ratonTiempoUso];
  return puntuacion;
}

export function calcularPuntuacionTeclado(input: EntradaRatonTecladoRosa): number {
  let puntuacion = PUNTUACION_TECLADO_BASE[input.teclado];
  if (input.tecladoDesviacionAlEscribir) puntuacion += 1;
  if (input.tecladoElevadoHombrosEncogidos) puntuacion += 1;
  if (input.tecladoAlcancePorEncimaCabeza) puntuacion += 1;
  if (input.tecladoSoporteNoAjustable) puntuacion += 1;
  puntuacion += PUNTUACION_TIEMPO_USO[input.tecladoTiempoUso];
  return puntuacion;
}

// Tabla C ROSA (ratón x teclado) — verificada idéntica entre Cornell
// y NTP 1173. Filas = Ratón (0-7, clampeado), columnas = Teclado
// (0-7, clampeado). No modificar sin repetir esa verificación.
const TABLA_C_RATON_TECLADO: number[][] = [
  [1, 1, 1, 2, 3, 4, 5, 6],
  [1, 1, 2, 3, 4, 5, 6, 7],
  [1, 2, 2, 3, 4, 5, 6, 7],
  [2, 3, 3, 3, 5, 6, 7, 8],
  [3, 4, 4, 5, 5, 6, 7, 8],
  [4, 5, 5, 6, 6, 7, 8, 9],
  [5, 6, 6, 7, 7, 8, 8, 9],
  [6, 7, 7, 8, 8, 9, 9, 9],
];

export function calcularCeldaTablaC(raton: number, teclado: number): number {
  const fila = clamp(raton, 0, 7);
  const columna = clamp(teclado, 0, 7);
  return TABLA_C_RATON_TECLADO[fila][columna];
}

export function calcularPuntuacionSeccionC(input: EntradaRatonTecladoRosa): number {
  const raton = calcularPuntuacionRaton(input);
  const teclado = calcularPuntuacionTeclado(input);
  return calcularCeldaTablaC(raton, teclado);
}

// ============================================================
// SECCIÓN D: PANTALLA/TELÉFONO + RATÓN/TECLADO
// ============================================================

// Tabla D ROSA (pantalla+periféricos = Sección B x Sección C) —
// verificada idéntica entre Cornell y NTP 1173. Filas = Sección B
// (1-9, clampeado), columnas = Sección C (1-9, clampeado). No
// modificar sin repetir esa verificación.
const TABLA_D_PANTALLA_PERIFERICOS: number[][] = [
  [1, 2, 3, 4, 5, 6, 7, 8, 9],
  [2, 2, 3, 4, 5, 6, 7, 8, 9],
  [3, 3, 3, 4, 5, 6, 7, 8, 9],
  [4, 4, 4, 4, 5, 6, 7, 8, 9],
  [5, 5, 5, 5, 5, 6, 7, 8, 9],
  [6, 6, 6, 6, 6, 6, 7, 8, 9],
  [7, 7, 7, 7, 7, 7, 7, 8, 9],
  [8, 8, 8, 8, 8, 8, 8, 8, 9],
  [9, 9, 9, 9, 9, 9, 9, 9, 9],
];

export function calcularPuntuacionPantallaPerifericos(seccionB: number, seccionC: number): number {
  const fila = clamp(seccionB, 1, 9) - 1;
  const columna = clamp(seccionC, 1, 9) - 1;
  return TABLA_D_PANTALLA_PERIFERICOS[fila][columna];
}

// ============================================================
// PUNTUACIÓN FINAL (TABLA E)
// ============================================================

// Tabla E ROSA (puntuación final = Silla x Pantalla/Periféricos) —
// verificada idéntica entre Cornell y NTP 1173. Filas = Silla con
// tiempo (1-10, clampeado), columnas = Pantalla/Periféricos (1-10,
// clampeado). No modificar sin repetir esa verificación.
const TABLA_E_FINAL: number[][] = [
  [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  [2, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  [3, 3, 3, 4, 5, 6, 7, 8, 9, 10],
  [4, 4, 4, 4, 5, 6, 7, 8, 9, 10],
  [5, 5, 5, 5, 5, 6, 7, 8, 9, 10],
  [6, 6, 6, 6, 6, 6, 7, 8, 9, 10],
  [7, 7, 7, 7, 7, 7, 7, 8, 9, 10],
  [8, 8, 8, 8, 8, 8, 8, 8, 9, 10],
  [9, 9, 9, 9, 9, 9, 9, 9, 9, 10],
  [10, 10, 10, 10, 10, 10, 10, 10, 10, 10],
];

export function calcularCeldaTablaE(silla: number, pantallaPerifericos: number): number {
  const fila = clamp(silla, 1, 10) - 1;
  const columna = clamp(pantallaPerifericos, 1, 10) - 1;
  return TABLA_E_FINAL[fila][columna];
}

// ============================================================
// EVALUACIÓN COMPLETA
// ============================================================

export interface EvaluacionRosaInput {
  silla: EntradaSillaRosa;
  pantallaTelefono: EntradaPantallaTelefonoRosa;
  ratonTeclado: EntradaRatonTecladoRosa;
}

export interface ResultadoRosa {
  puntuacionSilla: number;
  puntuacionSeccionB: number;
  puntuacionSeccionC: number;
  puntuacionPantallaPerifericos: number;
  puntuacionFinal: number;
  nivelAccion: 'sin_accion_inmediata' | 'accion_requerida'; // >=5 según la NTP y el paper original
}

export function calcularEvaluacionRosa(input: EvaluacionRosaInput): ResultadoRosa {
  const puntuacionSilla = calcularPuntuacionSilla(input.silla);
  const puntuacionSeccionB = calcularPuntuacionSeccionB(input.pantallaTelefono);
  const puntuacionSeccionC = calcularPuntuacionSeccionC(input.ratonTeclado);
  const puntuacionPantallaPerifericos = calcularPuntuacionPantallaPerifericos(puntuacionSeccionB, puntuacionSeccionC);

  const puntuacionFinal = calcularCeldaTablaE(puntuacionSilla, puntuacionPantallaPerifericos);

  return {
    puntuacionSilla,
    puntuacionSeccionB,
    puntuacionSeccionC,
    puntuacionPantallaPerifericos,
    puntuacionFinal,
    nivelAccion: puntuacionFinal >= 5 ? 'accion_requerida' : 'sin_accion_inmediata',
  };
}

// ============================================================
// Verificación manual — ejemplo resuelto de la NTP 1173 (caso de referencia)
// ============================================================
// Silla:
//   A-1 = calcularPuntuacionA1('alto_rodillas_mayor_90', true, false)
//       = 2 (base) + 1 (alturaNoRegulable) = 3
//   A-2 = calcularPuntuacionA2('neutra_8cm', false) = 1
//   A-3 = calcularPuntuacionA3('desviacion_alto_o_bajo', false, true, false)
//       = 2 (base) + 1 (reposabrazosNoRegulables) = 3
//   A-4 = calcularPuntuacionA4('sin_apoyo_lumbar_o_inadecuado', false, true)
//       = 2 (base) + 1 (respaldoNoRegulable) = 3
//   sumaAlturaProfundidad = 3+1 = 4 → fila = clamp(4,2,8)-2 = 2
//   sumaReposabrazosRespaldo = 3+3 = 6 → columna = clamp(6,2,9)-2 = 4
//   TABLA_A_SILLA[2][4] = 5
//   + PUNTUACION_TIEMPO_USO['mas_1h_o_mas_4h_dia'] (+1) = 6
//   puntuacionSilla esperado = 6
//
// Teléfono (Sección B-1):
//   calcularPuntuacionTelefono({ telefono: 'neutro_1mano_manosLibres',
//     telefonoSujecionHombroCuello: false, telefonoSinManosLibres: false,
//     telefonoTiempoUso: 'entre_30min_1h_o_1_4h_dia', ... })
//   = 1 (base) + 0 = 1
//
// Pantalla (Sección B-2):
//   calcularPuntuacionPantalla({ pantalla: 'neutra_40_75cm_altura_ojos',
//     pantallaDistanciaMayor75cm: false, pantallaGiroCuello: false,
//     pantallaSinPortadocumentos: true, pantallaReflejos: false,
//     pantallaTiempoUso: 'mas_1h_o_mas_4h_dia', ... })
//   = 1 (base) + 1 (pantallaSinPortadocumentos) + 1 (tiempo) = 3
//
//   puntuacionSeccionB = TABLA_B_TELEFONO_PANTALLA[clamp(1,0,6)][clamp(3,0,8)]
//                      = TABLA_B_TELEFONO_PANTALLA[1][3] = 2
//   puntuacionSeccionB esperado = 2
//
// Ratón (Sección C-1):
//   calcularPuntuacionRaton({ raton: 'desviacion_no_alineado',
//     ratonPequenoAgarrePinza: false, ratonTecladoDiferentesAlturas: false,
//     ratonReposamanosDuro: true, ratonTiempoUso: 'mas_1h_o_mas_4h_dia', ... })
//   = 2 (base) + 1 (ratonReposamanosDuro) + 1 (tiempo) = 4
//
// Teclado (Sección C-2):
//   calcularPuntuacionTeclado({ teclado: 'neutro_muneca_recta',
//     tecladoDesviacionAlEscribir: false, tecladoElevadoHombrosEncogidos: false,
//     tecladoAlcancePorEncimaCabeza: false, tecladoSoporteNoAjustable: false,
//     tecladoTiempoUso: 'entre_30min_1h_o_1_4h_dia', ... })
//   = 1 (base) + 0 (tiempo) = 1
//
//   puntuacionSeccionC = TABLA_C_RATON_TECLADO[clamp(4,0,7)][clamp(1,0,7)]
//                      = TABLA_C_RATON_TECLADO[4][1] = 4
//   puntuacionSeccionC esperado = 4
//
// Pantalla/periféricos (Tabla D):
//   calcularPuntuacionPantallaPerifericos(2, 4)
//   = TABLA_D_PANTALLA_PERIFERICOS[clamp(2,1,9)-1][clamp(4,1,9)-1]
//   = TABLA_D_PANTALLA_PERIFERICOS[1][3] = 4
//   puntuacionPantallaPerifericos esperado = 4
//
// Puntuación final (Tabla E):
//   TABLA_E_FINAL[clamp(6,1,10)-1][clamp(4,1,10)-1] = TABLA_E_FINAL[5][3] = 6
//   puntuacionFinal esperado = 6 → nivelAccion = 'accion_requerida' (6 >= 5)
//
// Resultado esperado (confirmado a mano antes de guardar este archivo):
//   puntuacionSilla=6, puntuacionSeccionB=2, puntuacionSeccionC=4,
//   puntuacionPantallaPerifericos=4, puntuacionFinal=6,
//   nivelAccion='accion_requerida' — coincide con el ejemplo resuelto
//   de la NTP 1173 (2022).
// ============================================================
